import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square, Trash2, FolderOpen } from 'lucide-react';
import { musicStrings as S } from '../../content/music';
import { encodeWav } from './recordingAudio';

const MAX_BYTES = 6 * 1024 * 1024;
const MAX_SECONDS = 60;
const FORMATS = new Set(['m4a', 'mp3', 'wav']);

/** Preview and validate a local clip before the parent saves its moment. */
export default function MusicAudioDraft({ file, onChange, hasExistingRecording = false }) {
  const picker = useRef(null);
  const capture = useRef(null);
  const mounted = useRef(true);
  const [preview, setPreview] = useState('');
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!file) { setPreview(''); return undefined; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; release(); };
  }, []);

  function release() {
    const state = capture.current; capture.current = null;
    if (!state) return null;
    clearTimeout(state.timeout); clearInterval(state.tick);
    if (state.media?.state === 'recording') { state.media.onstop = null; state.media.stop(); }
    state.node?.disconnect(); state.source?.disconnect();
    state.stream?.getTracks().forEach(track => track.stop());
    state.context?.close().catch(() => {});
    return state;
  }

  async function chooseFile(candidate) {
    if (!candidate) return;
    const extension = candidate.name.split('.').pop().toLowerCase();
    if (!FORMATS.has(extension)) { setError(S.audioUnsupported); return; }
    if (!candidate.size || candidate.size > MAX_BYTES) { setError(S.audioTooLarge); return; }
    const objectURL = URL.createObjectURL(candidate);
    try {
      const duration = await new Promise((resolve, reject) => {
        const audio = new Audio();
        audio.preload = 'metadata';
        audio.onloadedmetadata = () => resolve(audio.duration);
        audio.onerror = reject;
        audio.src = objectURL;
      });
      if (!Number.isFinite(duration) || duration <= 0 || duration > MAX_SECONDS + 0.1) { setError(S.audioTooLong); return; }
      setError(''); onChange(candidate);
    } catch { setError(S.audioUnsupported); }
    finally { URL.revokeObjectURL(objectURL); }
  }

  function stop() {
    const state = capture.current;
    if (!state) return;
    if (state.media) {
      state.media.onstop = () => {
        if (mounted.current && state.chunks.length) onChange(new File(state.chunks, 'moment.m4a', { type: 'audio/mp4' }));
      };
      state.media.stop();
      release();
    } else {
      release();
      if (state.chunks.length) onChange(new File([encodeWav(state.chunks)], 'moment.wav', { type: 'audio/wav' }));
    }
    setRecording(false);
  }

  async function start() {
    setError(''); setSeconds(0);
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1 } });
      const aacType = window.MediaRecorder?.isTypeSupported('audio/mp4;codecs=mp4a.40.2') ? 'audio/mp4;codecs=mp4a.40.2' : null;
      if (aacType) {
        const chunks = [];
        const media = new MediaRecorder(stream, { mimeType: aacType, audioBitsPerSecond: 64000 });
        capture.current = { stream, media, chunks };
        media.ondataavailable = event => { if (event.data.size) chunks.push(event.data); };
        media.start();
      } else {
        if (!window.AudioWorkletNode) throw new Error('unsupported');
        const context = new AudioContext({ sampleRate: 44100 });
        if (context.sampleRate !== 44100) { await context.close(); throw new Error('unsupported'); }
        capture.current = { stream, context, chunks: [], samples: 0 };
        await context.audioWorklet.addModule('/audio/practice-recorder.js'); await context.resume();
        const state = capture.current;
        state.source = context.createMediaStreamSource(stream);
        state.node = new AudioWorkletNode(context, 'practice-recorder');
        state.node.port.onmessage = ({ data }) => {
          if (capture.current !== state) return;
          const chunk = data.slice(0, Math.max(0, 44100 * MAX_SECONDS - state.samples));
          state.chunks.push(chunk); state.samples += chunk.length;
          if (state.samples >= 44100 * MAX_SECONDS) stop();
        };
        state.source.connect(state.node); state.node.connect(context.destination);
      }
      const state = capture.current;
      state.timeout = setTimeout(stop, MAX_SECONDS * 1000);
      state.tick = setInterval(() => setSeconds(value => Math.min(MAX_SECONDS, value + 1)), 1000);
      setRecording(true);
    } catch {
      if (!capture.current) stream?.getTracks().forEach(track => track.stop());
      release(); setError('Microphone recording is unavailable. Check browser permission or choose an audio file.');
    }
  }

  return <section className="recipe-detail-panel music-audio-draft"><h3>{S.recording}</h3>
    {hasExistingRecording && !file && <p>{S.replaceAudioHint}</p>}
    {file && preview && <audio controls src={preview} preload="metadata" />}
    {recording && <p role="status">{seconds}s / {MAX_SECONDS}s</p>}
    <div className="form-actions">
      {recording ? <button type="button" className="btn-secondary" onClick={stop}><Square size={18} /> Stop recording</button> : !file && <><button type="button" className="btn-secondary" onClick={start}><Mic size={18} /> Record</button><button type="button" className="btn-secondary" onClick={() => picker.current?.click()}><FolderOpen size={18} /> {S.chooseAudio}</button></>}
      {file && <button type="button" className="btn-danger music-recording-delete" onClick={() => onChange(null)} title={S.removeAudio} aria-label={S.removeAudio}><Trash2 size={18} /></button>}
    </div>
    <input ref={picker} type="file" accept=".m4a,.mp3,.wav,audio/mp4,audio/mpeg,audio/wav" hidden onChange={event => { chooseFile(event.target.files?.[0]); event.target.value = ''; }} />
    {error && <p className="error-copy" role="alert">{error}</p>}
  </section>;
}
